import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Spinner, Overline } from "@/components/Widgets";
import { PageGuide } from "@/components/PageGuide";
import { XLogo, LinkedinLogo, InstagramLogo, Broadcast, CheckCircle, LinkSimple } from "@phosphor-icons/react";

const META = {
  x: { label: "X (Twitter)", Icon: XLogo, note: "Posting usually requires a paid X API plan." },
  linkedin: { label: "LinkedIn", Icon: LinkedinLogo, note: "Needs LinkedIn app-review approval for posting." },
  instagram: { label: "Instagram", Icon: InstagramLogo, note: "Needs a Business/Creator account + Facebook Page + Meta review." },
};

export default function Social() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/social/connections").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (params.get("connected")) { toast.success(`${META[params.get("connected")]?.label || "Account"} connected`); load(); }
    if (params.get("error")) { toast.error(`Could not connect ${params.get("error")}. Check the account requirements.`); }
    if (params.get("connected") || params.get("error")) setParams({}, { replace: true });
  }, [params]);

  const connect = async (p) => {
    setBusy(p);
    try {
      const { data } = await api.get(`/social/${p}/connect`);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy("");
    }
  };

  const disconnect = async (p) => {
    await api.delete(`/social/${p}`);
    toast.success("Disconnected");
    load();
  };

  const toggleAuto = async () => {
    const next = !data.autopost_enabled;
    setData({ ...data, autopost_enabled: next });
    try {
      await api.post("/social/autopost", { enabled: next });
      toast.success(next ? "Auto-posting ON — new articles will post to your feeds" : "Auto-posting paused");
    } catch { setData({ ...data, autopost_enabled: !next }); toast.error("Could not update"); }
  };

  if (!data) return <Spinner label="Loading social accounts…" />;
  const anyConnected = data.platforms.some((p) => p.connected);

  return (
    <div className="space-y-6 fade-up" data-testid="social-page">
      <PageGuide title="Flood your social feeds" steps={[
        "Connect X, LinkedIn and Instagram below (each needs its own developer app + approval).",
        "Turn on Auto-posting so every new rebuild article is shared to your connected feeds automatically.",
        "Not configured yet? Ask your admin to add each platform's API keys — the buttons unlock once keys are set.",
      ]} />

      <Card testid="autopost-card">
        <div className="flex items-center gap-3">
          <button type="button" onClick={toggleAuto} disabled={!anyConnected} data-testid="social-autopost-toggle"
            className={`relative h-5 w-9 rounded-full transition-colors duration-200 shrink-0 disabled:opacity-40 ${data.autopost_enabled ? "bg-primary" : "bg-secondary border border-border"}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all duration-200 ${data.autopost_enabled ? "left-4" : "left-0.5"}`} />
          </button>
          <div className="flex-1">
            <div className="text-sm font-medium flex items-center gap-2">
              <Broadcast size={16} weight="fill" className="text-primary" /> Auto-post new articles to my feeds
              {data.autopost_enabled && anyConnected && <span className="text-emerald-400 text-xs">● ON</span>}
            </div>
            <div className="text-xs text-muted-foreground">When ON, every article your rebuild publishes is also shared to your connected social accounts.</div>
          </div>
        </div>
        {!anyConnected && <div className="text-xs text-muted-foreground mt-3">Connect at least one account below to enable auto-posting.</div>}
      </Card>

      <div className="grid sm:grid-cols-3 gap-4 stagger">
        {data.platforms.map((p) => {
          const m = META[p.platform];
          const Icon = m.Icon;
          return (
            <Card key={p.platform} testid={`social-card-${p.platform}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={26} weight="fill" className="text-primary" />
                <h3 className="font-head font-bold text-lg">{m.label}</h3>
                {p.connected && <CheckCircle size={18} weight="fill" className="text-emerald-400 ml-auto" />}
              </div>
              {p.connected ? (
                <>
                  <div className="text-sm text-emerald-400 flex items-center gap-1 mb-3"><LinkSimple size={13} /> {p.handle || "Connected"}</div>
                  <button onClick={() => disconnect(p.platform)} data-testid={`disconnect-${p.platform}`}
                    className="w-full border border-border py-2 text-sm hover:border-destructive hover:text-destructive transition-colors duration-200">Disconnect</button>
                </>
              ) : p.configured ? (
                <>
                  <p className="text-xs text-muted-foreground mb-3">{m.note}</p>
                  <button onClick={() => connect(p.platform)} disabled={busy === p.platform} data-testid={`connect-${p.platform}`}
                    className="w-full bg-primary text-primary-foreground font-semibold py-2 text-sm hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50">
                    {busy === p.platform ? "Redirecting…" : `Connect ${m.label}`}
                  </button>
                </>
              ) : (
                <>
                  <Overline className="text-muted-foreground mb-2">NOT CONFIGURED</Overline>
                  <p className="text-xs text-muted-foreground">{m.note} Add this platform's API keys on the server to unlock connecting.</p>
                </>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
